<template>
  <div class="wizard-step-2">
    <div class="section">
      <h4>
        <i class="pi pi-globe"></i>
        {{ t('Áreas de Atuação') }}
      </h4>

      <div v-if="hasAreas" class="areas-container">
        <div v-if="areasAtuacao?.Countries?.length" class="area-section">
          <div class="area-header">
            <i class="pi pi-globe"></i>
            <h5>{{ t('Países') }}</h5>
            <span class="count">{{ areasAtuacao.Countries.length }}</span>
          </div>
          <div class="area-items">
            <span v-for="country in areasAtuacao.Countries" :key="country.ID" class="area-tag country">
              {{ country.Name }}
              <i class="pi pi-times remove-icon" @click="removeItem('country', country.ID)"></i>
            </span>
          </div>
        </div>

        <div v-if="areasAtuacao?.States?.length" class="area-section">
          <div class="area-header">
            <i class="pi pi-map"></i>
            <h5>{{ t('Estados') }}</h5>
            <span class="count">{{ areasAtuacao.States.length }}</span>
          </div>
          <div class="area-items">
            <span v-for="state in areasAtuacao.States" :key="state.ID" class="area-tag state">
              {{ state.Name }} ({{ state.UF }})
              <i class="pi pi-times remove-icon" @click="removeItem('state', state.ID)"></i>
            </span>
          </div>
        </div>

        <div v-if="areasAtuacao?.Cities?.length" class="area-section">
          <div class="area-header">
            <i class="pi pi-building"></i>
            <h5>{{ t('Cidades') }}</h5>
            <span class="count">{{ areasAtuacao.Cities.length }}</span>
          </div>
          <div class="area-items">
            <span v-for="city in displayedCities" :key="city.ID" class="area-tag city">
              {{ city.Name }}
              <small v-if="city.StateID">({{ city.StateUF }})</small>
              <i class="pi pi-times remove-icon" @click="removeItem('city', city.ID)"></i>
            </span>
            <span v-if="hasMoreCities" class="area-tag more" @click="toggleShowAll">
              {{ showAllCities ? t('Mostrar menos') : `+${remainingCitiesCount} ${t('mais')}` }}
            </span>
          </div>
        </div>

        <div v-if="areasAtuacao?.Neighborhoods?.length" class="area-section">
          <div class="area-header">
            <i class="pi pi-home"></i>
            <h5>{{ t('Bairros') }}</h5>
            <span class="count">{{ areasAtuacao.Neighborhoods.length }}</span>
          </div>
          <div class="area-items">
            <span
              v-for="neighborhood in areasAtuacao.Neighborhoods"
              :key="neighborhood.ID"
              class="area-tag neighborhood"
            >
              {{ neighborhood.Name }}
              <small v-if="getNeighborhoodMeta(neighborhood)">
                ({{ getNeighborhoodMeta(neighborhood) }})
              </small>
              <i class="pi pi-times remove-icon" @click="removeItem('neighborhood', neighborhood.ID)"></i>
            </span>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="pi pi-globe"></i>
        <p>{{ t('Nenhuma área de atuação configurada') }}</p>
      </div>

      <div class="edit-section">
        <button class="edit-toggle-btn" data-testid="franquias-wizard-step2-edit-toggle" @click.stop="toggleEditMode">
          <i class="pi" :class="isEditMode ? 'pi-times' : 'pi-pencil'"></i>
          <span>{{ isEditMode ? t('Fechar Edição') : t('Adicionar Áreas') }}</span>
        </button>
      </div>

      <div v-if="isEditMode" class="edit-panel">
        <div class="browser-breadcrumb">
          <span
            v-for="(crumb, index) in browserPath"
            :key="`${crumb.level}-${crumb.id ?? 'root'}-${index}`"
            class="breadcrumb-item"
            :class="{ active: index === browserPath.length - 1 }"
            @click="index < browserPath.length - 1 ? navigateToBreadcrumb(index) : null"
          >
            <i v-if="index === 0" class="pi pi-globe"></i>
            <span>{{ crumb.name }}</span>
            <i v-if="index < browserPath.length - 1" class="pi pi-chevron-right separator"></i>
          </span>
        </div>

        <div class="browser-search">
          <input-text
            data-testid="franquias-wizard-step2-search"
            v-model="browserSearch"
            :placeholder="t('Buscar...')"
            elem-id="browser-search"
            fluid
          />
        </div>

        <div v-if="browserLoading" class="browser-loading">
          <i class="pi pi-spinner pi-spin"></i>
          <span>{{ t('Carregando...') }}</span>
        </div>

        <div v-else-if="filteredBrowserItems.length === 0" class="browser-empty">
          <i class="pi pi-search"></i>
          <span>{{ browserSearch ? t('Nenhum resultado encontrado') : t('Nenhum item disponível') }}</span>
        </div>

        <div v-else class="browser-list">
          <div
            v-for="item in filteredBrowserItems"
            :key="item.ID"
            class="browser-item"
            :class="{ added: isItemAdded(item) }"
          >
            <div class="item-info">
              <span class="item-name">{{ item.Name }}</span>
              <span v-if="item.UF" class="item-detail">{{ item.UF }}</span>
            </div>
            <div class="item-actions">
              <button
                :data-testid="`franquias-wizard-step2-toggle-${item.ID}`"
                class="action-btn"
                :class="isItemAdded(item) ? 'remove' : 'add'"
                :disabled="isLoading"
                @click="toggleItem(item)"
              >
                <i :class="isItemAdded(item) ? 'pi pi-check' : 'pi pi-plus'"></i>
                {{ isItemAdded(item) ? t('Remover') : t('Adicionar') }}
              </button>
              <button
                :data-testid="`franquias-wizard-step2-drill-${item.ID}`"
                v-if="browserLevel !== 'neighborhoods'"
                class="action-btn drill"
                :disabled="browserLoading"
                @click="drillDown(item)"
              >
                {{ drillDownLabel }}
                <i class="pi pi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="info-card">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('Configure os países, estados, cidades e bairros onde a franquia atua. Use "Adicionar Áreas" para expandir sua atuação.') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useLocalidades } from '../../composables/useLocalidades';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface LocationItem {
  ID?: Id;
  Name?: string;
  UF?: string;
  CityID?: Id;
  CityName?: string;
  StateID?: Id;
  StateUF?: string;
  StateName?: string;
  CountryID?: Id;
  CountryName?: string;
  [key: string]: unknown;
}

interface AreasAtuacao {
  Countries?: LocationItem[];
  States?: LocationItem[];
  Cities?: LocationItem[];
  Neighborhoods?: LocationItem[];
  [key: string]: unknown;
}

interface BreadcrumbItem {
  level: BrowserLevel;
  id: Id | null;
  name: string;
}

type BrowserLevel = 'countries' | 'states' | 'cities' | 'neighborhoods';

interface Props {
  franquiaId?: number | null;
  areasAtuacao?: AreasAtuacao | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
  areasAtuacao: null,
});

const emit = defineEmits<{
  'validation-change': [isValid: boolean];
  'refresh-areas': [];
}>();

const { t } = useI18n();
const { fetchPaises, fetchEstados, fetchCidades, fetchBairros, toggleRelacao } = useLocalidades();

const showAllCities = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isLoading = ref<boolean>(false);

const availablePaises = ref<LocationItem[]>([]);

const browserLevel = ref<BrowserLevel>('countries');
const browserPath = ref<BreadcrumbItem[]>([{ level: 'countries', id: null, name: t('Países') }]);
const browserItems = ref<LocationItem[]>([]);
const browserLoading = ref<boolean>(false);
const browserSearch = ref<string>('');

const countryIds = computed(() => props.areasAtuacao?.Countries?.map((country) => country.ID) || []);
const stateIds = computed(() => props.areasAtuacao?.States?.map((state) => state.ID) || []);
const cityIds = computed(() => props.areasAtuacao?.Cities?.map((city) => city.ID) || []);
const neighborhoodIds = computed(() => props.areasAtuacao?.Neighborhoods?.map((neighborhood) => neighborhood.ID) || []);

const hasAreas = computed(() => {
  return !!props.areasAtuacao && (
    (props.areasAtuacao.Countries && props.areasAtuacao.Countries.length > 0) ||
    (props.areasAtuacao.States && props.areasAtuacao.States.length > 0) ||
    (props.areasAtuacao.Cities && props.areasAtuacao.Cities.length > 0) ||
    (props.areasAtuacao.Neighborhoods && props.areasAtuacao.Neighborhoods.length > 0)
  );
});

const displayedCities = computed(() => {
  if (!props.areasAtuacao?.Cities) {
    return [];
  }

  if (showAllCities.value) {
    return props.areasAtuacao.Cities;
  }

  return props.areasAtuacao.Cities.slice(0, 10);
});

const hasMoreCities = computed<boolean>(() => (props.areasAtuacao?.Cities?.length || 0) > 10);

const remainingCitiesCount = computed(() => {
  if (!props.areasAtuacao?.Cities) {
    return 0;
  }

  return props.areasAtuacao.Cities.length - 10;
});

const drillDownLabel = computed(() => {
  if (browserLevel.value === 'countries') {
    return t('Ver estados');
  }

  if (browserLevel.value === 'states') {
    return t('Ver cidades');
  }

  if (browserLevel.value === 'cities') {
    return t('Ver bairros');
  }

  return '';
});

const filteredBrowserItems = computed(() => {
  if (!browserSearch.value) {
    return browserItems.value;
  }

  const query = browserSearch.value.toLowerCase();
  return browserItems.value.filter((item) => {
    const name = (item.Name || '').toLowerCase();
    const uf = (item.UF || '').toLowerCase();
    return name.includes(query) || uf.includes(query);
  });
});

const getCountryName = (countryId: Id | undefined): string => {
  if (!countryId) {
    return '';
  }

  return props.areasAtuacao?.Countries?.find((country) => String(country.ID) === String(countryId))?.Name || '';
};

const getStateName = (stateId: Id | undefined): string => {
  if (!stateId) {
    return '';
  }

  const state = props.areasAtuacao?.States?.find((item) => String(item.ID) === String(stateId));
  if (!state) {
    return '';
  }

  return state.UF ? `${state.Name} (${state.UF})` : (state.Name || '');
};

const getNeighborhoodMeta = (neighborhood: LocationItem): string => {
  const cityName = neighborhood.CityName
    || props.areasAtuacao?.Cities?.find((city) => String(city.ID) === String(neighborhood.CityID))?.Name
    || '';
  const stateName = neighborhood.StateName
    || getStateName(neighborhood.StateID)
    || '';
  const countryName = neighborhood.CountryName
    || getCountryName(neighborhood.CountryID)
    || '';

  return [cityName, stateName, countryName].filter(Boolean).join(' • ');
};

const toggleShowAll = (): void => {
  showAllCities.value = !showAllCities.value;
};

const loadPaises = async (): Promise<void> => {
  try {
    const data = (await fetchPaises()) as LocationItem[] | null;
    availablePaises.value = data || [];
    log('FranquiasWizardStep2', 'loadPaises', 'Países carregados', availablePaises.value);
  } catch (err) {
    log('FranquiasWizardStep2', 'loadPaises', 'Erro ao carregar países', err);
    availablePaises.value = [];
  }
};

const navigateToCountries = async (): Promise<void> => {
  browserLevel.value = 'countries';
  browserPath.value = [{ level: 'countries', id: null, name: t('Países') }];
  browserSearch.value = '';
  browserLoading.value = true;

  try {
    if (!availablePaises.value.length) {
      await loadPaises();
    }

    browserItems.value = availablePaises.value;
  } catch (err) {
    log('FranquiasWizardStep2', 'navigateToCountries', 'Erro ao navegar para países', err);
    browserItems.value = [];
  } finally {
    browserLoading.value = false;
  }
};

const navigateToStates = async (countryId: Id | null, countryName: string): Promise<void> => {
  browserLevel.value = 'states';
  browserPath.value = [
    { level: 'countries', id: null, name: t('Países') },
    { level: 'states', id: countryId, name: countryName },
  ];
  browserSearch.value = '';
  browserLoading.value = true;

  try {
    browserItems.value = (await fetchEstados(Number(countryId))) as LocationItem[];
  } catch (err) {
    log('FranquiasWizardStep2', 'navigateToStates', 'Erro ao navegar para estados', err);
    browserItems.value = [];
  } finally {
    browserLoading.value = false;
  }
};

const navigateToCities = async (stateId: Id | null, stateName: string): Promise<void> => {
  browserLevel.value = 'cities';
  browserPath.value = [
    ...browserPath.value.slice(0, 2),
    { level: 'cities', id: stateId, name: stateName },
  ];
  browserSearch.value = '';
  browserLoading.value = true;

  try {
    browserItems.value = (await fetchCidades(Number(stateId))) as LocationItem[];
  } catch (err) {
    log('FranquiasWizardStep2', 'navigateToCities', 'Erro ao navegar para cidades', err);
    browserItems.value = [];
  } finally {
    browserLoading.value = false;
  }
};

const navigateToNeighborhoods = async (cityId: Id | null, cityName: string): Promise<void> => {
  browserLevel.value = 'neighborhoods';
  browserPath.value = [
    ...browserPath.value.slice(0, 3),
    { level: 'neighborhoods', id: cityId, name: cityName },
  ];
  browserSearch.value = '';
  browserLoading.value = true;

  try {
    browserItems.value = (await fetchBairros(Number(cityId))) as LocationItem[];
  } catch (err) {
    log('FranquiasWizardStep2', 'navigateToNeighborhoods', 'Erro ao navegar para bairros', err);
    browserItems.value = [];
  } finally {
    browserLoading.value = false;
  }
};

const navigateToBreadcrumb = async (index: number): Promise<void> => {
  const crumb = browserPath.value[index];

  if (!crumb) {
    return;
  }

  if (crumb.level === 'countries') {
    await navigateToCountries();
  } else if (crumb.level === 'states') {
    await navigateToStates(crumb.id, crumb.name);
  } else if (crumb.level === 'cities') {
    await navigateToCities(crumb.id, crumb.name);
  }
};

const drillDown = async (item: LocationItem): Promise<void> => {
  if (browserLevel.value === 'countries') {
    await navigateToStates(item.ID ?? null, item.Name || '');
  } else if (browserLevel.value === 'states') {
    await navigateToCities(item.ID ?? null, item.Name || '');
  } else if (browserLevel.value === 'cities') {
    await navigateToNeighborhoods(item.ID ?? null, item.Name || '');
  }
};

const isItemAdded = (item: LocationItem): boolean => {
  if (browserLevel.value === 'countries') {
    return item.ID !== undefined && countryIds.value.includes(item.ID);
  }

  if (browserLevel.value === 'states') {
    return item.ID !== undefined && stateIds.value.includes(item.ID);
  }

  if (browserLevel.value === 'cities') {
    return item.ID !== undefined && cityIds.value.includes(item.ID);
  }

  if (browserLevel.value === 'neighborhoods') {
    return item.ID !== undefined && neighborhoodIds.value.includes(item.ID);
  }

  return false;
};

const levelTypeMap: Record<BrowserLevel, string> = {
  countries: 'country',
  states: 'state',
  cities: 'city',
  neighborhoods: 'neighborhood',
};

const toggleItem = async (item: LocationItem): Promise<void> => {
  if (!props.franquiaId || item.ID === undefined) {
    return;
  }

  isLoading.value = true;

  try {
    await toggleRelacao({
      type: levelTypeMap[browserLevel.value],
      localidade_id: Number(item.ID),
      franquia_id: props.franquiaId,
    });

    eventBus.emit(
      'globalMessage.success',
      isItemAdded(item) ? t('Item removido com sucesso') : t('Item adicionado com sucesso'),
    );
    emit('refresh-areas');
  } catch (err) {
    log('FranquiasWizardStep2', 'toggleItem', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao atualizar item'));
  } finally {
    isLoading.value = false;
  }
};

const removeItem = async (type: string, id: Id | undefined): Promise<void> => {
  if (!props.franquiaId || id === undefined) {
    return;
  }

  isLoading.value = true;

  try {
    await toggleRelacao({
      type: type,
      localidade_id: Number(id),
      franquia_id: props.franquiaId,
    });

    eventBus.emit('globalMessage.success', t('Item removido com sucesso'));
    emit('refresh-areas');
  } catch (err) {
    log('FranquiasWizardStep2', 'removeItem', 'Erro ao remover item', err);
    eventBus.emit('globalMessage.error', t('Erro ao remover item'));
  } finally {
    isLoading.value = false;
  }
};

const toggleEditMode = async (): Promise<void> => {
  isEditMode.value = !isEditMode.value;
  log('FranquiasWizardStep2', 'toggleEditMode', 'Modo de edição alterado', isEditMode.value);

  if (isEditMode.value) {
    await navigateToCountries();
  }
};

onMounted(async () => {
  await loadPaises();
  emit('validation-change', true);
});
</script>

<style scoped>
.wizard-step-2 {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 20px;
}

.section h4 {
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 10px;
}

.section h4 i {
  width: 28px;
  height: 28px;
  background: rgba(var(--app-secondary-color-rgb), 0.12);
  color: var(--tenant-secondary-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.areas-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.area-section {
  background: var(--surface-50);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--surface-border);
  transition: all 0.2s ease;
}

.area-section:hover {
  border-color: var(--tenant-secondary-color);
  box-shadow: 0 2px 8px rgba(var(--app-secondary-color-rgb), 0.1);
}

.area-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.area-header i {
  width: 32px;
  height: 32px;
  background: var(--surface-100);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.area-section:nth-child(1) .area-header i {
  color: var(--tenant-secondary-color);
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}

.area-section:nth-child(2) .area-header i {
  color: #22c55e;
  background: #dcfce7;
}

.area-section:nth-child(3) .area-header i {
  color: var(--tenant-secondary-color);
  background: rgba(var(--app-secondary-color-rgb), 0.12);
}

.area-section:nth-child(4) .area-header i {
  color: #ea580c;
  background: #ffedd5;
}

.area-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  flex: 1;
}

.area-header .count {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-color-secondary);
  background: var(--surface-200);
  padding: 4px 10px;
  border-radius: 12px;
}

.area-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.area-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.area-tag.country {
  background: rgba(var(--app-secondary-color-rgb), 0.12);
  color: var(--tenant-secondary-color);
  border: 1px solid rgba(var(--app-secondary-color-rgb), 0.24);
}

.area-tag.state {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
}

.area-tag.city {
  background: rgba(var(--app-secondary-color-rgb), 0.08);
  color: var(--tenant-secondary-color);
  border: 1px solid rgba(var(--app-secondary-color-rgb), 0.2);
}

.area-tag.neighborhood {
  background: #ffedd5;
  color: #c2410c;
  border: 1px solid #fdba74;
}

.area-tag small {
  font-size: 11px;
  opacity: 0.7;
}

.area-tag.more {
  background: var(--surface-200);
  color: var(--text-color);
  border: 1px solid var(--surface-300);
  cursor: pointer;
}

.area-tag.more:hover {
  background: var(--surface-300);
}

.area-tag .remove-icon {
  margin-left: 6px;
  font-size: 12px;
  opacity: 0.5;
  cursor: pointer;
  transition: all 0.2s;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.area-tag .remove-icon:hover {
  opacity: 1;
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  text-align: center;
  background: var(--surface-50);
  border-radius: 12px;
  border: 1px dashed var(--surface-border);
}

.empty-state i {
  font-size: 40px;
  color: var(--text-color-secondary);
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.edit-section {
  display: flex;
  justify-content: flex-start;
  margin: 12px 0;
  position: relative;
  z-index: 10;
}

.edit-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(
    135deg,
    var(--tenant-secondary-color),
    var(--tenant-secondary-hover-color, var(--tenant-secondary-color))
  );
  border: none;
  color: #ffffff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(var(--app-secondary-color-rgb), 0.22);
}

.edit-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(var(--app-secondary-color-rgb), 0.28);
}

.edit-panel {
  background: var(--surface-50);
  border: 2px solid var(--tenant-secondary-color);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  z-index: 1;
  margin-top: 16px;
  box-shadow: 0 4px 20px rgba(var(--app-secondary-color-rgb), 0.1);
}

.browser-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  background: var(--surface-100);
  border-radius: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-color-secondary);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.breadcrumb-item:hover:not(.active) {
  background: var(--surface-200);
  color: var(--text-color);
}

.breadcrumb-item.active {
  color: var(--tenant-secondary-color);
  font-weight: 600;
  cursor: default;
}

.breadcrumb-item .separator {
  font-size: 10px;
  color: var(--text-color-secondary);
  opacity: 0.5;
}

.browser-search {
  margin-bottom: 12px;
}

.browser-search :deep(.input-text-wrapper) {
  margin-bottom: 0;
}

.browser-loading,
.browser-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.browser-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
}

.browser-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--surface-card);
  transition: all 0.15s;
  border-bottom: 1px solid var(--surface-100);
  gap: 12px;
}

.browser-item:last-child {
  border-bottom: none;
}

.browser-item:hover {
  background: var(--surface-50);
}

.browser-item.added {
  background: rgba(var(--app-secondary-color-rgb), 0.06);
}

.item-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
}

.item-detail {
  font-size: 12px;
  color: var(--text-color-secondary);
  background: var(--surface-200);
  padding: 2px 8px;
  border-radius: 4px;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--surface-card);
  color: var(--text-color);
}

.action-btn.add {
  border-color: var(--tenant-secondary-color);
  color: var(--tenant-secondary-color);
}

.action-btn.add:hover:not(:disabled) {
  background: var(--tenant-secondary-color);
  color: #ffffff;
}

.action-btn.remove {
  border-color: #22c55e;
  color: #22c55e;
  background: #f0fdf4;
}

.action-btn.remove:hover:not(:disabled) {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
}

.action-btn.drill {
  border-color: var(--surface-300);
  color: var(--text-color-secondary);
}

.action-btn.drill:hover:not(:disabled) {
  border-color: var(--tenant-secondary-color);
  color: var(--tenant-secondary-color);
  background: rgba(var(--app-secondary-color-rgb), 0.08);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    var(--tenant-secondary-color),
    var(--tenant-secondary-hover-color, var(--tenant-secondary-color))
  );
  border: 1px solid var(--tenant-secondary-hover-color, var(--tenant-secondary-color));
  border-radius: 12px;
  color: #ffffff;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-card i {
  color: #ffffff;
  font-size: 18px;
  flex-shrink: 0;
}

.info-card span {
  line-height: 1.5;
}

@media (max-width: 768px) {
  .edit-panel {
    padding: 16px;
  }

  .area-items {
    gap: 8px;
  }

  .area-tag {
    padding: 6px 12px;
    font-size: 12px;
  }

  .browser-item {
    align-items: flex-start;
    flex-direction: column;
  }

  .item-actions {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
